import { useSyncExternalStore } from 'react'

// The shell deliberately has NO top-level react-router Router: five of the
// seven surfaces mount their own <BrowserRouter basename="/x"> (per the
// federation-era contract), and routers cannot nest. The shell only needs to
// know which basePath prefix is active, so it watches location.pathname
// directly and swaps surfaces on prefix changes.

const NAV_EVENT = 'omni-shell:navigate'

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange)
  window.addEventListener(NAV_EVENT, onChange)
  return () => {
    window.removeEventListener('popstate', onChange)
    window.removeEventListener(NAV_EVENT, onChange)
  }
}

function getPathname(): string {
  return window.location.pathname
}

/** Reactive location.pathname (shell-initiated navigations + back/forward). */
export function usePathname(): string {
  return useSyncExternalStore(subscribe, getPathname)
}

function getSearch(): string {
  return window.location.search
}

/** Reactive location.search, for a page that opens on a query (?lead=, ?view=). */
export function useSearch(): string {
  return useSyncExternalStore(subscribe, getSearch)
}

/**
 * Shell-side navigation: pushState + notify. Cross-origin hrefs fall back to
 * a full page load. Surface-internal navigation stays inside each surface's
 * own router and never comes through here.
 */
export function shellNavigate(href: string): void {
  const url = new URL(href, window.location.origin)
  if (url.origin !== window.location.origin) {
    window.location.assign(url.toString())
    return
  }
  window.history.pushState(null, '', url.pathname + url.search + url.hash)
  window.dispatchEvent(new Event(NAV_EVENT))
}
